import {
  ORIGINAL_COMPAT_CHECKPOINT_RETENTION_MS,
  ORIGINAL_COMPAT_CHECKPOINT_VERSION,
  type CheckpointIntegrityResult,
  type OriginalCompatCheckpointBody,
  type OriginalCompatCheckpointMetadata,
  type OriginalCompatResumeStage,
} from './compatTypes';
import { computeAuthoritativeInputHash, computeRoadmapHash } from './compatHash.service';
import type { Chapter, EditorResult, Ledger, PublicationEditorResult, StoryBible, ValidationResult } from '../../types';
import type { OriginalOrchestrationLoopState } from '../golden/goldenOrchestrationLoop';

export async function buildCompatCheckpointBody(input: {
  projectId: string;
  chapterNumber: number;
  runId: string;
  resumeStage: OriginalCompatResumeStage;
  storyBible: StoryBible;
  ledger: Ledger;
  chapterDraft: Chapter;
  originalRetryCount: number;
  inspectorResult?: ValidationResult;
  correctionOrder?: string;
  loopState?: OriginalOrchestrationLoopState;
  editorResult?: EditorResult;
  publicationResult?: PublicationEditorResult;
  updatedLedger?: Ledger;
  now?: number;
}): Promise<OriginalCompatCheckpointBody> {
  const now = input.now ?? Date.now();
  const [authoritativeInputHash, roadmapHash] = await Promise.all([
    computeAuthoritativeInputHash({
      storyBible: input.storyBible,
      ledger: input.ledger,
      chapterNumber: input.chapterNumber,
    }),
    computeRoadmapHash(input.storyBible),
  ]);
  return {
    checkpointVersion: ORIGINAL_COMPAT_CHECKPOINT_VERSION,
    engineMode: 'ORIGINAL_ENGINE_COMPAT',
    projectId: input.projectId,
    chapterNumber: input.chapterNumber,
    runId: input.runId,
    resumeStage: input.resumeStage,
    chapterDraft: input.chapterDraft,
    inspectorResult: input.inspectorResult,
    correctionOrder: input.correctionOrder,
    originalRetryCount: input.originalRetryCount,
    loopState: input.loopState,
    editorResult: input.editorResult,
    publicationResult: input.publicationResult,
    updatedLedger: input.updatedLedger,
    authoritativeInputHash,
    roadmapHash,
    createdAt: new Date(now).toISOString(),
    expiresAt: new Date(now + ORIGINAL_COMPAT_CHECKPOINT_RETENTION_MS).toISOString(),
    canonical: false,
  };
}

export async function validateCheckpointIntegrity(input: {
  checkpoint: OriginalCompatCheckpointMetadata | OriginalCompatCheckpointBody;
  storyBible: StoryBible;
  ledger: Ledger;
  chapterNumber: number;
  now?: number;
}): Promise<CheckpointIntegrityResult> {
  const now = input.now ?? Date.now();
  if (input.checkpoint.engineMode !== 'ORIGINAL_ENGINE_COMPAT') {
    return { ok: false, reason: 'engine_mode' };
  }
  if (input.checkpoint.checkpointVersion !== ORIGINAL_COMPAT_CHECKPOINT_VERSION) {
    return { ok: false, reason: 'version_mismatch' };
  }
  if (new Date(input.checkpoint.expiresAt).getTime() < now) {
    return { ok: false, reason: 'expired' };
  }
  const [expectedRoadmap, expectedInput] = await Promise.all([
    computeRoadmapHash(input.storyBible),
    computeAuthoritativeInputHash({
      storyBible: input.storyBible,
      ledger: input.ledger,
      chapterNumber: input.chapterNumber,
    }),
  ]);
  if (input.checkpoint.roadmapHash !== expectedRoadmap || input.checkpoint.authoritativeInputHash !== expectedInput) {
    return { ok: false, reason: 'hash_mismatch' };
  }
  return { ok: true };
}

export function resolveNextStageAfterResume(stage: OriginalCompatResumeStage): OriginalCompatResumeStage | 'COMMIT' | null {
  switch (stage) {
    case 'SCRIBE_COMPLETE':
      return 'INSPECTOR_COMPLETE';
    case 'INSPECTOR_COMPLETE':
      return 'EDITOR_COMPLETE';
    case 'EDITOR_COMPLETE':
      return 'PUBLICATION_EDITOR_COMPLETE';
    case 'PUBLICATION_EDITOR_COMPLETE':
      return 'ARCHIVIST_COMPLETE';
    case 'ARCHIVIST_COMPLETE':
    case 'COMMIT_PENDING':
      return 'COMMIT';
    default:
      return null;
  }
}

export function compatCheckpointStoragePath(
  uid: string,
  projectId: string,
  chapterNumber: number,
  runId: string,
): string {
  return `users/${uid}/projects/${projectId}/compat-checkpoints/ch${chapterNumber}-${runId}.json`;
}

export function compatCheckpointDocId(chapterNumber: number): string {
  return `compat-stage-ch${chapterNumber}`;
}
